"use client";

import React from 'react';
import type { LabelKey } from '@/lib/data/labels';

/**
 * Institution filter shown under the programme search box. `key` is the
 * lowercased `ProgrammeResult.university` value, so a button matches results
 * directly; ALL_UNIVERSITIES clears the filter.
 *
 * Listed in JUPAS institution-code order (CityU = 1 … HKMU = 9), which is the
 * order students see everywhere else in the application.
 */
export const ALL_UNIVERSITIES = 'all';

export const UNIVERSITY_FILTER_OPTIONS = [
    { key: 'cityu', label: 'CityU' },
    { key: 'hkbu', label: 'HKBU' },
    { key: 'polyu', label: 'PolyU' },
    { key: 'cuhk', label: 'CUHK' },
    { key: 'hkust', label: 'HKUST' },
    { key: 'hku', label: 'HKU' },
    { key: 'lingnan', label: 'LingU' },
    { key: 'eduhk', label: 'EdUHK' },
    { key: 'hkmu', label: 'HKMU' },
    { key: 'sssdp', label: 'SSSDP' },
] as const;

interface UniversityFilterButtonsProps {
    universityFilter: string;
    setUniversityFilter: (key: string) => void;
    translate: (key: LabelKey) => string;
}

export const UniversityFilterButtons: React.FC<UniversityFilterButtonsProps> = ({
    universityFilter,
    setUniversityFilter,
    translate,
}) => {
    // The abbreviations are used as-is in both languages — they are how the
    // institutions are referred to in Chinese JUPAS material too.
    const options = [
        { key: ALL_UNIVERSITIES, label: translate('all') },
        ...UNIVERSITY_FILTER_OPTIONS,
    ];

    return (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label={translate('university')}>
            {options.map(({ key, label }) => {
                const isActive = universityFilter === key;
                return (
                    <button
                        key={key}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => setUniversityFilter(key)}
                        className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition-colors md:text-sm ${
                            isActive
                                ? 'border-amber-600 bg-amber-50 text-amber-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        {label}
                    </button>
                );
            })}
        </div>
    );
};
